import React, { useCallback, useMemo, useState } from "react";
import { ClipboardPaste, Copy, Check, Download, Eraser, FlaskConical, Trash2 } from "lucide-react";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { cleanText, inspectText } from "../lib/unicodeScrub";
import { disruptStatistical } from "../lib/statisticalScrub";
import { downloadBytes } from "../lib/processFile";
import { utf8Encode } from "../lib/bytes";
import { useI18n } from "../i18n";
import { reportCleaned } from "../lib/stats";

const SAMPLE =
  "Voici\u200b un texte\u00a0qui semble normal.\u200d\nBut it hides\u2060 invisible carriers\u200b — zero\u200cwidth joiners, a BOM\ufeff, bidi\u202emarks\u202c and\u2003em spaces.\nFurthermore, it is important to note that we utilize numerous approaches in order to demonstrate a robust and comprehensive result.\nEmoji stay intact: 👨‍👩‍👧 ❤️‍🔥 ⚖️";

const KIND_COLOR = {
  space: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  confusable: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-400/10",
};
const DEFAULT_COLOR = "text-rose-300 border-rose-400/30 bg-rose-400/10";

function Highlighted({ text, kinds }) {
  const nodes = [];
  let buf = "";
  let i = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    const kind = kinds.get(cp);
    if (kind) {
      if (buf) { nodes.push(buf); buf = ""; }
      nodes.push(
        <mark key={`m${i}`} className={kind === "space" ? "carrier-space" : "carrier"} title={`U+${cp.toString(16).toUpperCase()}`}>
          {kind === "space" ? "␣" : "·"}
        </mark>
      );
    } else buf += ch;
    i++;
  }
  if (buf) nodes.push(buf);
  return <>{nodes}</>;
}

export default function TextScrubber() {
  const { t } = useI18n();
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [opts, setOpts] = useState({ normalizeSpaces: true, nfkc: false, aggressive: false, stripEmojiGlue: false, statistical: false });
  const [intensity, setIntensity] = useState("balanced");
  const [highlight, setHighlight] = useState(true);

  const report = useMemo(
    () => inspectText(input, { aggressive: opts.aggressive, stripEmojiGlue: opts.stripEmojiGlue }),
    [input, opts.aggressive, opts.stripEmojiGlue]
  );

  const kindMap = useMemo(() => {
    const m = new Map();
    for (const h of report.hits) m.set(h.codepoint, h.kind);
    return m;
  }, [report]);

  const run = useCallback(() => {
    const out = cleanText(input, {
      nfkc: opts.nfkc,
      aggressiveHomoglyphs: opts.aggressive,
      normalizeSpaces: opts.normalizeSpaces,
      stripEmojiGlue: opts.stripEmojiGlue,
    });
    let stat = null;
    let text = out.text;
    if (opts.statistical) {
      const s = disruptStatistical(text, { intensity });
      text = s.text;
      stat = s.stats;
    }
    setResult({ ...out, text, stat });
    reportCleaned(1, out.stats.removedCount + out.stats.replacedCount + (stat ? stat.tokensChanged : 0));
  }, [input, opts, intensity]);

  const setOpt = (k) => (v) => setOpts((o) => ({ ...o, [k]: v }));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      toast.success(t("text.copied"));
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      toast.error("clipboard blocked");
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
      <div className="space-y-4">
        <div className="rounded-xl border border-white/[0.07] bg-[#0c0c0e]">
          <div className="flex flex-wrap items-center gap-2 border-b border-white/[0.07] px-4 py-2.5">
            <span className="mr-auto text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("text.inputLabel")}</span>
            <button
              data-testid="text-paste-btn"
              onClick={async () => { try { setInput(await navigator.clipboard.readText()); } catch (e) { toast.error("clipboard blocked"); } }}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
            >
              <ClipboardPaste className="h-3.5 w-3.5" strokeWidth={1.75} /> {t("text.paste")}
            </button>
            <button
              data-testid="text-sample-btn"
              onClick={() => { setInput(SAMPLE); setResult(null); }}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
            >
              <FlaskConical className="h-3.5 w-3.5" strokeWidth={1.75} /> {t("text.sample")}
            </button>
            <button
              data-testid="text-clear-btn"
              onClick={() => { setInput(""); setResult(null); }}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} /> {t("text.clear")}
            </button>
          </div>
          <textarea
            data-testid="text-input"
            value={input}
            onChange={(e) => { setInput(e.target.value); setResult(null); }}
            placeholder={t("text.placeholder")}
            spellCheck={false}
            className="scroll-thin h-56 w-full resize-y bg-transparent px-4 py-3.5 font-mono2 text-[13px] leading-relaxed text-slate-200 outline-none placeholder:text-slate-600"
          />
          <div className="flex items-center gap-4 border-t border-white/[0.07] px-4 py-2 text-[11px] text-slate-500">
            <span data-testid="text-char-count">{report.length} {t("text.chars")}</span>
            <span data-testid="text-suspicious-count" className={report.suspiciousTotal ? "text-rose-400" : ""}>
              {report.suspiciousTotal} {t("text.suspicious")}
            </span>
            <button
              data-testid="text-highlight-toggle"
              onClick={() => setHighlight((h) => !h)}
              className={`ml-auto rounded-md px-2 py-0.5 transition-colors ${highlight ? "text-sky-400" : "hover:text-white"}`}
            >
              {t("text.highlight")}
            </button>
          </div>
          {highlight && input && (
            <div data-testid="text-highlight-view" className="scroll-thin max-h-40 overflow-auto whitespace-pre-wrap break-words border-t border-white/[0.07] px-4 py-3 font-mono2 text-[13px] leading-relaxed text-slate-400">
              <Highlighted text={input} kinds={kindMap} />
            </div>
          )}
        </div>

        <button
          data-testid="text-clean-btn"
          disabled={!input}
          onClick={run}
          className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-6 py-2.5 font-medium text-white shadow-[0_0_20px_rgba(14,165,233,0.3)] transition-colors hover:bg-sky-400 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-slate-500 disabled:shadow-none active:scale-[0.98]"
        >
          <Eraser className="h-4 w-4" strokeWidth={1.75} /> {t("text.clean")}
        </button>

        {result && (
          <div data-testid="text-result-panel" className="rise rounded-xl border border-sky-500/20 bg-[#0c0c0e]">
            <div className="flex flex-wrap items-center gap-3 border-b border-white/[0.07] px-4 py-2.5">
              <span className="mr-auto text-[10px] font-bold uppercase tracking-[0.2em] text-sky-400">{t("text.result")}</span>
              <span className="font-mono2 text-[11px] text-slate-400">
                <span className="text-rose-400">{result.stats.removedCount}</span> {t("text.removed")} ·{" "}
                <span className="text-amber-300">{result.stats.replacedCount}</span> {t("text.replaced")}
                {result.stat && (
                  <>
                    {" · "}
                    <span data-testid="stat-tokens" className="text-sky-300">{result.stat.tokensChanged}</span> {t("text.statTokens")}{" "}
                    ({(result.stat.ratio * 100).toFixed(1)}% {t("text.statRatio")})
                  </>
                )}
              </span>
              <button data-testid="text-copy-btn" onClick={copy} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />} {t("text.copy")}
              </button>
              <button
                data-testid="text-download-btn"
                onClick={() => downloadBytes(utf8Encode(result.text), "cleaned.txt", "text/plain;charset=utf-8")}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
              >
                <Download className="h-3.5 w-3.5" strokeWidth={1.75} /> {t("text.download")}
              </button>
            </div>
            <pre data-testid="text-result" className="scroll-thin max-h-72 overflow-auto whitespace-pre-wrap break-words px-4 py-3.5 font-mono2 text-[13px] leading-relaxed text-slate-200">
              {result.text}
            </pre>
            {result.stat && (
              <div data-testid="stat-score" className="border-t border-white/[0.07] px-4 py-3">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("text.favoured")}</p>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-mono2 text-[12px]">
                  <span className="text-rose-400">{result.stat.before.per1000.toFixed(1)}</span>
                  <span className="text-slate-600">→</span>
                  <span className="text-emerald-400">{result.stat.after.per1000.toFixed(1)}</span>
                  <span className="text-slate-500">{t("text.per1000")}</span>
                  <span data-testid="stat-reduction" className="ml-auto rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-300">
                    −{Math.round(result.stat.reduction * 100)}% {t("text.reduction")}
                  </span>
                </div>
                {result.stat.applied.length > 0 && (
                  <ul data-testid="stat-applied" className="mt-3 flex flex-wrap gap-1.5">
                    {result.stat.applied.map((a, i) => (
                      <li key={i} className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono2 text-[10px] text-slate-400">{a}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <aside className="space-y-4">
        <div className="rounded-xl border border-white/[0.07] bg-[#0c0c0e] p-4">
          <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("text.opts")}</p>
          <div className="space-y-3.5">
            {[
              ["normalizeSpaces", "text.optSpaces"],
              ["nfkc", "text.optNfkc"],
              ["aggressive", "text.optConf"],
              ["stripEmojiGlue", "text.optGlue"],
              ["statistical", "text.optStat"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-start justify-between gap-3">
                <Label htmlFor={`opt-${key}`} className="text-[13px] font-normal leading-snug text-slate-300">{t(label)}</Label>
                <Switch id={`opt-${key}`} data-testid={`opt-${key}`} checked={opts[key]} onCheckedChange={setOpt(key)} className="data-[state=checked]:bg-sky-500" />
              </div>
            ))}
          </div>
          {opts.statistical && (
            <div className="mt-4 border-t border-white/[0.07] pt-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("text.intensity")}</p>
              <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-0.5" data-testid="stat-intensity">
                {["light", "balanced", "strong"].map((s) => (
                  <button
                    key={s}
                    data-testid={`intensity-${s}`}
                    onClick={() => setIntensity(s)}
                    className={`flex-1 rounded-full px-2 py-1 text-[11px] font-medium transition-colors ${
                      intensity === s ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {t(`int.${s}`)}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-slate-500">{t("text.statNote")}</p>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-white/[0.07] bg-[#0c0c0e] p-4">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("text.report")}</p>
          {report.hits.length === 0 ? (
            <p data-testid="text-no-hits" className="text-[13px] leading-relaxed text-slate-500">{t("text.noHits")}</p>
          ) : (
            <ul data-testid="text-hits-list" className="scroll-thin max-h-[26rem] space-y-2 overflow-auto pr-1">
              {report.hits.map((h) => (
                <li key={`${h.codepoint}-${h.kind}`} className="rounded-lg border border-white/[0.07] bg-white/[0.02] p-2.5">
                  <div className="flex items-center gap-2">
                    <code className="font-mono2 text-[11px] text-sky-300">{h.code}</code>
                    <span className={`rounded border px-1.5 py-px text-[10px] ${KIND_COLOR[h.kind] || DEFAULT_COLOR}`}>{t(`kind.${h.kind}`)}</span>
                    <span className="ml-auto font-mono2 text-[11px] text-slate-400">×{h.count}</span>
                  </div>
                  <p className="mt-1.5 text-[11px] leading-snug text-slate-500">{h.label.replace(h.code, "").trim()}</p>
                  <p className="mt-1 font-mono2 text-[10px] text-slate-600">
                    {t(`conf.${h.confidence}`)} · @ {h.sampleOffsets.slice(0, 5).join(", ")}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
